import {
  ArrowLeftRegularIcon,
  ArrowRightRegularIcon,
  ChevronDownRegularIcon,
  CircleExclamationRegularIcon,
} from "@elixir/icons";
import React, { useEffect, useMemo, useState } from "react";
import {
  ExpandingTextarea,
  FormField,
  InputFormField,
  LinkButton,
  OptionChoice,
  PrimaryButton,
} from "ui";
import usePostCreateHealthFacility from "../../../apis/axon/hfr/use-create-hfr";
import useHprHfrOnboardingStore from "../../../stores/hpr-hfr-dhis-onboarding";
import {
  HFROnboardingSubStates,
  OnboardingNavigationStates,
} from "../../../stores/hpr-hfr-dhis-onboarding/enums";
import {
  ClinicsEntity,
  HFR_HPR_MIXPANEL_EVENTS,
  CLINIC_VERIFY_TYPE,
} from "@elixir/types";
import { onHfrCreatedSuccessfully } from "../../../utils/bridges/methods";
import { track } from "../../../utils/tracking";
import getB from "../../../utils/bridges/EkaBridge";
import { Radio } from "antd";
import useGetSuggestFacilities, {
  Facility,
} from "apis/src/axon/hpr/get-suggest-facilities";
import usePostLinkHealthFacility from "../../../apis/axon/hfr/use-link-hfr";

function HfrForm() {
  const FACILITIES_PER_PAGE = 3;
  const [clinicVerifyType, setClinicVerifyType] = useState(
    CLINIC_VERIFY_TYPE.SUGGESTED,
  );
  const createdHprId = useHprHfrOnboardingStore((state) => state.createdHprId);
  const selectedDoctorId = useHprHfrOnboardingStore((state) => state.doctorId);
  const hprIdNumber = useHprHfrOnboardingStore((state) => state.hprIdNumber);

  const doctorProfile = getB()
    .getMe()
    .docs.find((doc) => doc.id === selectedDoctorId);

  useEffect(() => {
    track(HFR_HPR_MIXPANEL_EVENTS.CREATE_HFR_PAGE_VIEW);
  }, []);

  const clinicId = useHprHfrOnboardingStore((state) => state.selectedClinicId);
  const setSelectedClinicId = useHprHfrOnboardingStore(
    (state) => state.setClinicId,
  );
  const setSubStateScreen = useHprHfrOnboardingStore(
    (state) => state.setSubActiveScreen,
  );

  const [formState, setFormState] = useState<{
    clinicName?: string;
    clinicPincode?: string;
    clinicAddress?: string;
  }>({});

  const [formErrorState, setFormErrorState] = useState<{
    pincodeError?: string;
    clinicAddress?: string;
    formError?: string;
  }>({});

  const [suggestedClinicsList, setSuggestedClinicsList] = useState<Facility[]>(
    [],
  );

  const {
    data: suggestedClinics,
    error: suggestedClinicsError,
    isLoading: isSuggestedClinicsLoading,
  } = useGetSuggestFacilities(hprIdNumber || "");

  useEffect(() => {
    if (suggestedClinics) {
      if (suggestedClinics.data?.length === 0) {
        setClinicVerifyType(CLINIC_VERIFY_TYPE.CREATE_NEW);
      }
      setSuggestedClinicsList(suggestedClinics.data || []);
      setSelectedFacilityId(suggestedClinics.data?.[0]?.facility_id || null);
    }
  }, [suggestedClinics]);

  const [selectedFacilityId, setSelectedFacilityId] = useState<string | null>(
    suggestedClinicsList[0]?.facility_id ?? null,
  );

  const selectedFacility = useMemo(() => {
    return suggestedClinicsList.find(
      (clinic) => clinic.facility_id === selectedFacilityId,
    );
  }, [selectedFacilityId, suggestedClinicsList]);

  function setClinicData(clinic: ClinicsEntity) {
    if (!clinic) {
      return;
    }
    const { address } = clinic;
    const { line1, city, pin } = address || {};

    const clinicAddress = [line1 ? line1 : "", city ? city : ""]
      .filter(Boolean)
      .join(", ");

    if (clinicAddress) {
      const { error } = validateAddress(clinicAddress);

      if (error) {
        setFormErrorState({ clinicAddress: error, formError: undefined });
      }
    }

    setSelectedClinicId(clinic._id);

    setFormState((prev) => ({
      ...prev,
      clinicAddress: clinicAddress ? clinicAddress : "",
      clinicName: clinic.name,
      clinicPincode: pin ? `${pin}` : "",
    }));
  }


  const clinicsList = useMemo(() => {
    if (!doctorProfile) {
      return [];
    }
    return doctorProfile?.professional?.clinics || [];
  }, [doctorProfile]);

  
  useEffect(() => {
    if (clinicsList.length === 0) return;

    
    const clinicToSelect = clinicId
      ? clinicsList.find((item) => item._id === clinicId) || clinicsList[0]
      : clinicsList[0];

    
    if (clinicToSelect) {
      setClinicData(clinicToSelect as ClinicsEntity);
    }
  }, [clinicsList, clinicId]);

  const allowSubmit = useMemo(() => {
    if (clinicVerifyType === CLINIC_VERIFY_TYPE.SUGGESTED) {
      return !!selectedFacilityId;
    }

    if (!formState.clinicName) return false;
    if (!validatePincode(formState.clinicPincode || "").valid) return false;
    if (!validateAddress(formState.clinicAddress || "").valid) return false;

    return true;
  }, [
    clinicVerifyType,
    selectedFacilityId,
    formState.clinicName,
    formState.clinicPincode,
    formState.clinicAddress,
  ]);

  const { mutateAsync, isLoading } = usePostCreateHealthFacility();
  const { mutateAsync: mutateLinkAsync, isLoading: linkHealthFacilityLoading } =
    usePostLinkHealthFacility();

  const setActiveScreen = useHprHfrOnboardingStore(
    (state) => state.setActiveScreen,
  );
  const setHfrFacilityId = useHprHfrOnboardingStore(
    (state) => state.setHfrFacilityId,
  );

  const onSubmit = async () => {
    if (clinicVerifyType === CLINIC_VERIFY_TYPE.CREATE_NEW) {
      const { valid, error } = validateAddress(formState.clinicAddress || "");

      const newFormErrorState = { ...formErrorState, formError: undefined };

      if (!valid && error) {
        newFormErrorState.clinicAddress = error;
      } else {
        newFormErrorState.clinicAddress = undefined;
      }

      const { valid: isPincodeValid, error: pincodeError } = validatePincode(
        formState.clinicPincode || "",
      );

      if (!isPincodeValid && pincodeError) {
        newFormErrorState.pincodeError = pincodeError;
      } else {
        newFormErrorState.pincodeError = undefined;
      }

      setFormErrorState(newFormErrorState);

      if (
        !!newFormErrorState.clinicAddress ||
        !!newFormErrorState.pincodeError
      ) {
        return;
      }

      if (!selectedDoctorId) {
        setFormErrorState((prev) => ({
          ...prev,
          formError: "Doctor ID is missing. Please login again.",
        }));
        return;
      }

      if (
        formState &&
        clinicId &&
        formState.clinicName &&
        formState.clinicPincode &&
        formState.clinicAddress
      ) {
        const { data, success, error } = await mutateAsync({
          clinicId: clinicId,
          clinicAddress: formState.clinicAddress,
          clinicName: formState.clinicName,
          clinicPincode: formState.clinicPincode,
          doctorId: selectedDoctorId,
        });

        track(HFR_HPR_MIXPANEL_EVENTS.CREATE_HFR, {
          success: `${success}`,
          error: error,
        });

        if (success && data) {
          setHfrFacilityId(data.facility_id);
          onHfrCreatedSuccessfully();
          setActiveScreen(OnboardingNavigationStates.HFR_CREATION_COMPLETED);
        } else if (error) {
          setFormErrorState((prev) => ({
            ...prev,
            formError: error,
          }));
        }
      }
    }
    if (clinicVerifyType === CLINIC_VERIFY_TYPE.SUGGESTED && selectedFacility) {
      if (!selectedDoctorId) {
        setFormErrorState((prev) => ({
          ...prev,
          formError: "Doctor ID is missing. Please login again.",
        }));
        return;
      }
      if (clinicId && selectedFacility) {
        const { data, success, error } = await mutateLinkAsync({
          clinicId: clinicId,
          hfrId: selectedFacility.facility_id,
          doctorId: selectedDoctorId,
        });

        track(HFR_HPR_MIXPANEL_EVENTS.LINK_HFR, {
          success: `${success}`,
          error: error,
        });

        if (success && data) {
          setHfrFacilityId(selectedFacility.facility_id);
          onHfrCreatedSuccessfully();
          setActiveScreen(OnboardingNavigationStates.HFR_CREATION_COMPLETED);
        } else if (error) {
          setFormErrorState((prev) => ({
            ...prev,
            formError: error,
          }));
        }
      }
    }
  };

  /* ---------------- PAGINATION STATE ---------------- */

  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.ceil(
    suggestedClinicsList.length / FACILITIES_PER_PAGE,
  );

  const paginatedClinics = useMemo(() => {
    const start = (currentPage - 1) * FACILITIES_PER_PAGE;
    const end = start + FACILITIES_PER_PAGE;
    return suggestedClinicsList.slice(start, end);
  }, [suggestedClinicsList, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [suggestedClinicsList.length]);

  const isPaginationDisabled =
    suggestedClinicsList.length <= FACILITIES_PER_PAGE;

  const isPrevDisabled = isPaginationDisabled || currentPage === 1;

  const isNextDisabled = isPaginationDisabled || currentPage === totalPages;

  /* -------------------------------------------------- */

  return (
    <div className="dr-h-full dr-flex dr-flex-col dr-px-16 md:pr-0 md:dr-pl-56 dr-max-w-396 dr-pb-48">
      <p className="Title2Bold dr-mb-16 dr-pt-48">Verify your Clinic Details</p>

      {hprIdNumber && (
        <div className="dr-w-full dr-py-16 dr-border-b-1 dr-border-darwin-touch-neutral-100">
          <div className="dr-flex dr-bg-darwin-touch-neutral-50 dr-rounded-8 dr-p-2">
            {/* Your Facilities */}
            <button
              type="button"
              onClick={() => setClinicVerifyType(CLINIC_VERIFY_TYPE.SUGGESTED)}
              className={`dr-flex-1 dr-py-6 dr-rounded-8 dr-flex dr-items-center dr-justify-center ${
                clinicVerifyType === CLINIC_VERIFY_TYPE.SUGGESTED
                  ? "dr-bg-text-white dr-shadow-elevation-1000"
                  : ""
              }`}
            >
              <span
                className={`dr-text-base ${
                  clinicVerifyType === CLINIC_VERIFY_TYPE.SUGGESTED
                    ? "dr-font-700 dr-text-darwin-touch-primary-primary"
                    : "dr-font-normal dr-text-fg-subtle"
                }`}
              >
                Your Facilities
              </span>
            </button>

            {/* Create New */}
            <button
              type="button"
              onClick={() => setClinicVerifyType(CLINIC_VERIFY_TYPE.CREATE_NEW)}
              className={`dr-flex-1 dr-py-6 dr-rounded-8 dr-flex dr-items-center dr-justify-center ${
                clinicVerifyType === CLINIC_VERIFY_TYPE.CREATE_NEW
                  ? "dr-bg-text-white dr-shadow-elevation-1000"
                  : ""
              }`}
            >
              <span
                className={`dr-text-base ${
                  clinicVerifyType === CLINIC_VERIFY_TYPE.CREATE_NEW
                    ? "dr-font-700 dr-text-darwin-touch-primary-primary"
                    : "dr-font-normal dr-text-fg-subtle"
                }`}
              >
                Create New
              </span>
            </button>
          </div>
        </div>
      )}

      {clinicVerifyType === CLINIC_VERIFY_TYPE.SUGGESTED && hprIdNumber && (
        <div className="dr-pt-16">
          <div className="dr-flex dr-items-center dr-justify-between">
            <div className="TouchCalloutBold dr-text-darwin-touch-neutral-1000">
              Select your facility
            </div>
            <div className="TouchLabelRegular dr-text-darwin-touch-neutral-800">
              {suggestedClinicsList.length} facilities fetched from NHPR
              repository
            </div>
          </div>

          <div>
            {suggestedClinicsList.length === 0 && (
              <div className="dr-text-center dr-py-20 dr-text-darwin-touch-neutral-400">
                No Facilites found
              </div>
            )}
            {suggestedClinicsList.length > 0 &&
              paginatedClinics.map((clinic, idx) => {
                const isSelected = selectedFacilityId === clinic.facility_id;
                return (
                  <div
                    key={clinic.facility_id}
                    className={`dr-rounded-8 dr-border-1 dr-py-10 dr-my-10 dr-px-20 
                    hover:dr-cursor-pointer
                    ${
                      isSelected
                        ? "dr-border-darwin-touch-primary-primary"
                        : "dr-border-darwin-touch-neutral-200"
                    }`}
                    onClick={() => setSelectedFacilityId(clinic.facility_id)}
                  >
                    <div className="dr-flex dr-items-center dr-justify-between">
                      <div>
                        <div className="dr-text-darwin-touch-neutral-1000 BodyBody1Bold">
                          {idx + 1}. {clinic.facility_name}
                        </div>
                        <div className="BodyRegular dr-text-darwin-touch-neutral-800">
                          {clinic.facility_id}{" "}
                        </div>
                        <div className="BodyRegular dr-text-darwin-touch-neutral-800">
                          {clinic.district}, {clinic.state}
                        </div>
                      </div>
                      <div>
                        <Radio
                          className="dr-radio"
                          checked={isSelected}
                          onChange={() =>
                            setSelectedFacilityId(clinic.facility_id)
                          }
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {(clinicVerifyType === CLINIC_VERIFY_TYPE.CREATE_NEW || !hprIdNumber) && (
        <form className="dr-flex dr-flex-col ">
          <div className="dr-flex dr-flex-col dr-space-y-4">
            <InputFormField
              title="Clinic Name"
              isRequired
              type="text"
              value={formState.clinicName}
              onChange={(e) => {
                setFormState((prev) => ({
                  ...prev,
                  clinicName: e.target.value,
                }));
              }}
            />

            <InputFormField
              title="Clinic Pincode"
              isRequired
              type="number"
              value={formState.clinicPincode}
              error={formErrorState.pincodeError}
              onBlur={({ value: pincode }) => {
                const { error } = validatePincode(pincode);
                setFormErrorState((state) => ({
                  ...state,
                  pincodeError: error,
                }));
              }}
              onChange={(e) => {
                const { valid } = validatePincode(e.target.value);
                if (valid) {
                  setFormErrorState((prev) => ({
                    ...prev,
                    pincodeError: undefined,
                    formError: undefined,
                  }));
                }
                setFormState((prev) => ({
                  ...prev,
                  clinicPincode: e.target.value,
                }));
              }}
            />
            <div>
              <FormField title="Clinic Address" isRequired>
                <ExpandingTextarea
                  rows={2}
                  value={formState.clinicAddress}
                  error={formErrorState.clinicAddress}
                  onChange={(e) => {
                    const { valid } = validateAddress(e.target.value);
                    if (valid) {
                      setFormErrorState((prev) => ({
                        ...prev,
                        clinicAddress: undefined,
                        formError: undefined,
                      }));
                    }
                    setFormState((prev) => ({
                      ...prev,
                      clinicAddress: e.target.value,
                    }));
                  }}
                  onBlur={(e) => {
                    const address = e.target.value;
                    const { error } = validateAddress(address);
                    setFormErrorState((state) => ({
                      ...state,
                      clinicAddress: error,
                    }));
                  }}
                />
              </FormField>
            </div>
            <div>
              {formErrorState.formError ? (
                <div className="dr-flex dr-items-center dr-space-x-8 dr-text-darwin-red-primary dr-mt-2">
                  <CircleExclamationRegularIcon className="dr-w-12 dr-h-12" />
                  <p className="FootnoteRegular">{formErrorState.formError}</p>
                </div>
              ) : null}
            </div>
          </div>
        </form>
      )}

      {clinicVerifyType === CLINIC_VERIFY_TYPE.SUGGESTED && hprIdNumber && (
        <div className="dr-flex dr-justify-center dr-gap-20 dr-pt-10">
          <LinkButton
            disabledTextColor="dr-text-darwin-touch-neutral-400"
            isDisabled={isPrevDisabled}
            title={"Previous"}
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            IconComponent={() => (
              <ArrowLeftRegularIcon
                className={`${isPrevDisabled ? "dr-text-darwin-touch-neutral-400" : "dr-text-darwin-touch-primary-primary"} dr-w-12 dr-h-12 `}
              />
            )}
            buttonIconPosition="left"
          />
          <LinkButton
            disabledTextColor="dr-text-darwin-touch-neutral-400"
            isDisabled={isNextDisabled}
            title={"Next"}
            onClick={() => {
              setCurrentPage((p) => Math.min(p + 1, totalPages));
            }}
            IconComponent={() => (
              <ArrowRightRegularIcon
                className={`${isNextDisabled ? "dr-text-darwin-touch-neutral-400" : "dr-text-darwin-touch-primary-primary"} dr-w-12 dr-h-12 `}
              />
            )}
            buttonIconPosition="right"
          />
        </div>
      )}

      <div className="dr-flex dr-justify-between dr-pt-48">
        <div />
        <PrimaryButton
          title="Submit"
          isLoading={isLoading || linkHealthFacilityLoading}
          onClick={onSubmit}
          extraClass="dr-w-200"
          isDisabled={!allowSubmit || isLoading}
        />
      </div>
    </div>
  );
}

function validatePincode(pincode: string) {
  if (!pincode || pincode.length !== 6) {
    return { valid: false, error: "Pincode needs to be of atleast 6 digits" };
  }

  return { valid: true };
}

function validateAddress(address: string) {
  if (!address.trim() || (address?.length || 0) < 20) {
    return {
      valid: false,
      error: "Address should be of atleast 20 characters",
    };
  }

  if (address.length > 200) {
    return {
      valid: false,
      error: "Address cannot have more than 200 Characters",
    };
  }

  return {
    valid: true,
  };
}

export default HfrForm;
